import i18n from 'i18next'
import { z } from 'zod'

/**
 * Custom Zod error map that uses i18n translations.
 * This is set globally so all Zod schemas automatically use translated messages.
 */
export const zodErrorMap: z.ZodErrorMap = (issue, ctx) => {
  const t = i18n.t.bind(i18n)

  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === 'undefined' || issue.received === 'null') {
        return { message: t('validation.required', { ns: 'forms' }) }
      }
      return {
        message: t('validation.invalidType', {
          ns: 'forms',
          expected: issue.expected,
          received: issue.received,
          defaultValue: ctx.defaultError,
        }),
      }

    case z.ZodIssueCode.too_small:
      if (issue.type === 'string') {
        if (issue.minimum === 1) {
          return { message: t('validation.required', { ns: 'forms' }) }
        }
        return { message: t('validation.string.min', { ns: 'forms', min: issue.minimum }) }
      }
      if (issue.type === 'number') {
        if (issue.inclusive) {
          return { message: t('validation.number.min', { ns: 'forms', min: issue.minimum }) }
        }
        return {
          message: t('validation.number.greaterThan', {
            ns: 'forms',
            min: issue.minimum,
            defaultValue: `Must be greater than ${issue.minimum}`,
          }),
        }
      }
      if (issue.type === 'array') {
        return {
          message: t('validation.array.min', {
            ns: 'forms',
            min: issue.minimum,
            defaultValue: `Must have at least ${issue.minimum} item(s)`,
          }),
        }
      }
      return { message: ctx.defaultError }

    case z.ZodIssueCode.too_big:
      if (issue.type === 'string') {
        return { message: t('validation.string.max', { ns: 'forms', max: issue.maximum }) }
      }
      if (issue.type === 'number') {
        if (issue.inclusive) {
          return { message: t('validation.number.max', { ns: 'forms', max: issue.maximum }) }
        }
        return {
          message: t('validation.number.lessThan', {
            ns: 'forms',
            max: issue.maximum,
            defaultValue: `Must be less than ${issue.maximum}`,
          }),
        }
      }
      if (issue.type === 'array') {
        return {
          message: t('validation.array.max', {
            ns: 'forms',
            max: issue.maximum,
            defaultValue: `Must have at most ${issue.maximum} item(s)`,
          }),
        }
      }
      return { message: ctx.defaultError }

    case z.ZodIssueCode.invalid_string:
      if (issue.validation === 'email') {
        return { message: t('validation.string.email', { ns: 'forms' }) }
      }
      if (issue.validation === 'url') {
        return {
          message: t('validation.string.url', { ns: 'forms', defaultValue: 'Must be a valid URL' }),
        }
      }
      return { message: ctx.defaultError }

    case z.ZodIssueCode.custom:
      // Allow custom messages to pass through
      return { message: issue.message ?? ctx.defaultError }

    default:
      return { message: ctx.defaultError }
  }
}

/**
 * Initialize Zod with the custom error map.
 * Call this once at app startup.
 */
export function initZodErrorMap() {
  z.setErrorMap(zodErrorMap)
}
